import { api } from '@/shared/api/client';

// ---- Types (backend DTO-той тааруулсан) ----
export interface Warehouse {
  Id: string;
  Code: string;
  Name: string;
  Type: number;
  TypeLabel: string;
  Country?: string;
  City?: string;
  Address?: string;
  Phone?: string;
  IsActive: boolean;
}
export interface Driver {
  Id: string;
  Code: string;
  Name: string;
  Phone: string;
  VehicleNo?: string;
  FKAppUserId?: string;
  IsActive: boolean;
}
export interface Tariff {
  Id: string;
  Name: string;
  FKOriginWarehouseId?: string;
  FKOriginWarehouseName?: string;
  TransportMode: number;
  TransportModeLabel: string;
  Basis: number;
  BasisLabel: string;
  RatePerUnit: number;
  MinCharge: number;
  IsActive: boolean;
}
export interface CargoCustomer {
  Id: string;
  WarehouseCode: string;
  Name: string;
  Phone: string;
  Email?: string;
  Address?: string;
  FKAppUserId?: string;
  DefaultDeliveryMethod: number;
  DefaultDeliveryMethodLabel: string;
  IsActive: boolean;
  Note?: string;
}
export interface TrackingEvent {
  Id: string;
  EventType: number;
  EventTypeLabel: string;
  WarehouseName?: string;
  OccurredOn: string;
  Note?: string;
}
export interface ShipmentListItem {
  Id: string;
  TrackingNo: string;
  FKCargoCustomerId?: string;
  CustomerName?: string;
  WarehouseCode?: string;
  Status: number;
  StatusLabel: string;
  TransportMode: number;
  TransportModeLabel: string;
  WeightKg: number;
  VolumeM3: number;
  PieceCount: number;
  Charge: number;
  PaymentStatus: number;
  PaymentStatusLabel: string;
  DeliveryMethod: number;
  CurrentWarehouseName?: string;
  CreatedOn?: string;
}
export interface ShipmentDetail extends ShipmentListItem {
  CustomerPhone?: string;
  FKOriginWarehouseId?: string;
  FKCurrentWarehouseId?: string;
  Length: number;
  Width: number;
  Height: number;
  DeclaredContent?: string;
  FKConsolidationId?: string;
  ConsolidationNo?: string;
  ReceivedAtOriginOn?: string;
  ArrivedDomesticOn?: string;
  DeliveredOn?: string;
  Note?: string;
  TrackingEvents: TrackingEvent[];
}
export interface ConsolidationListItem {
  Id: string;
  ConsolidationNo: string;
  TransportMode: number;
  TransportModeLabel: string;
  FromWarehouseName?: string;
  ToWarehouseName?: string;
  Status: number;
  StatusLabel: string;
  DepartedOn?: string;
  ArrivedOn?: string;
  ShipmentCount: number;
  TotalWeightKg: number;
  TotalVolumeM3: number;
}
export interface ConsolidationDetail {
  Id: string;
  ConsolidationNo: string;
  TransportMode: number;
  FKFromWarehouseId?: string;
  FKToWarehouseId?: string;
  Status: number;
  StatusLabel: string;
  DepartedOn?: string;
  ArrivedOn?: string;
  Note?: string;
  Shipments: ShipmentListItem[];
}
export interface DeliveryListItem {
  Id: string;
  DeliveryNo: string;
  FKDriverId?: string;
  DriverName?: string;
  Status: number;
  StatusLabel: string;
  ScheduledOn?: string;
  DeliveredOn?: string;
  Address?: string;
  ShipmentCount: number;
}
export interface DeliveryDetail extends DeliveryListItem {
  Note?: string;
  Shipments: ShipmentListItem[];
}
export interface CargoDashboard {
  ReceivedToday: number;
  InTransit: number;
  ReadyForPickup: number;
  OutForDelivery: number;
  Undelivered: number;
  RevenuePaid: number;
  RevenueUnpaid: number;
  ByStatus: { Status: number; StatusLabel: string; Count: number }[];
}

// ---- Endpoints ----
export const cargoApi = {
  // master
  warehouses: () => api.get<Warehouse[]>('/api/cargo/warehouse'),
  saveWarehouse: (id: string | null, b: unknown) =>
    id ? api.put<Warehouse>(`/api/cargo/warehouse/${id}`, b) : api.post<Warehouse>('/api/cargo/warehouse', b),
  deleteWarehouse: (id: string) => api.del(`/api/cargo/warehouse/${id}`),

  drivers: () => api.get<Driver[]>('/api/cargo/driver'),
  saveDriver: (id: string | null, b: unknown) =>
    id ? api.put<Driver>(`/api/cargo/driver/${id}`, b) : api.post<Driver>('/api/cargo/driver', b),
  deleteDriver: (id: string) => api.del(`/api/cargo/driver/${id}`),

  tariffs: () => api.get<Tariff[]>('/api/cargo/tariff'),
  saveTariff: (id: string | null, b: unknown) =>
    id ? api.put<Tariff>(`/api/cargo/tariff/${id}`, b) : api.post<Tariff>('/api/cargo/tariff', b),
  deleteTariff: (id: string) => api.del(`/api/cargo/tariff/${id}`),

  customers: () => api.get<CargoCustomer[]>('/api/cargo/customer'),
  nextCustomerCode: () => api.get<{ Code: string }>('/api/cargo/customer/next-code'),
  saveCustomer: (id: string | null, b: unknown) =>
    id ? api.put<CargoCustomer>(`/api/cargo/customer/${id}`, b) : api.post<CargoCustomer>('/api/cargo/customer', b),
  deleteCustomer: (id: string) => api.del(`/api/cargo/customer/${id}`),

  // shipments
  shipments: (status?: number) =>
    api.get<ShipmentListItem[]>(`/api/cargo/shipment${status != null ? `?status=${status}` : ''}`),
  shipment: (id: string) => api.get<ShipmentDetail>(`/api/cargo/shipment/${id}`),
  nextTrackingNo: () => api.get<{ TrackingNo: string }>('/api/cargo/shipment/next-no'),
  saveShipment: (id: string | null, b: unknown) =>
    id ? api.put<ShipmentDetail>(`/api/cargo/shipment/${id}`, b) : api.post<ShipmentDetail>('/api/cargo/shipment', b),
  changeStatus: (id: string, b: unknown) => api.post<ShipmentDetail>(`/api/cargo/shipment/${id}/status`, b),
  scan: (trackingNo: string, b: unknown) =>
    api.post<ShipmentDetail>(`/api/cargo/shipment/scan/${encodeURIComponent(trackingNo)}`, b),
  deleteShipment: (id: string) => api.del(`/api/cargo/shipment/${id}`),

  // consolidations
  consolidations: () => api.get<ConsolidationListItem[]>('/api/cargo/consolidation'),
  consolidation: (id: string) => api.get<ConsolidationDetail>(`/api/cargo/consolidation/${id}`),
  saveConsolidation: (id: string | null, b: unknown) =>
    id
      ? api.put<ConsolidationDetail>(`/api/cargo/consolidation/${id}`, b)
      : api.post<ConsolidationDetail>('/api/cargo/consolidation', b),
  addShipments: (id: string, ids: string[]) =>
    api.post<ConsolidationDetail>(`/api/cargo/consolidation/${id}/add-shipments`, { ShipmentIds: ids }),
  removeShipments: (id: string, ids: string[]) =>
    api.post<ConsolidationDetail>(`/api/cargo/consolidation/${id}/remove-shipments`, { ShipmentIds: ids }),
  depart: (id: string) => api.post<ConsolidationDetail>(`/api/cargo/consolidation/${id}/depart`, {}),
  arrive: (id: string) => api.post<ConsolidationDetail>(`/api/cargo/consolidation/${id}/arrive`, {}),
  deleteConsolidation: (id: string) => api.del(`/api/cargo/consolidation/${id}`),

  // deliveries
  deliveries: () => api.get<DeliveryListItem[]>('/api/cargo/delivery'),
  delivery: (id: string) => api.get<DeliveryDetail>(`/api/cargo/delivery/${id}`),
  saveDelivery: (id: string | null, b: unknown) =>
    id ? api.put<DeliveryDetail>(`/api/cargo/delivery/${id}`, b) : api.post<DeliveryDetail>('/api/cargo/delivery', b),
  markDelivered: (id: string) => api.post<DeliveryDetail>(`/api/cargo/delivery/${id}/delivered`, {}),
  deleteDelivery: (id: string) => api.del(`/api/cargo/delivery/${id}`),

  // misc
  dashboard: () => api.get<CargoDashboard>('/api/cargo/report/dashboard'),
  ensureDefaults: () => api.post('/api/cargo/setup/ensure-defaults', {}),
};
